import { defineStore } from 'pinia'
import { ref } from 'vue'
import { supabase } from '@/lib/supabase'
import type { Database } from '../../shared/db/public.types'

export type Preset = Database["public"]["Tables"]["presets"]["Row"];
export type Model = Database["public"]["Tables"]["models"]["Row"];

export const useSystemStore = defineStore('system', () => {
  const presets = ref<Preset[]>([])
  const models = ref<Model[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)

  // --- Theme Logic ---
  const theme = ref(localStorage.getItem('theme') || 'light')
  const dashboardSidebarWidth = ref(parseInt(localStorage.getItem('dashboardSidebarWidth') || '256'))

  function setDashboardSidebarWidth(width: number) {
    dashboardSidebarWidth.value = width
    localStorage.setItem('dashboardSidebarWidth', width.toString())
  }

  function initTheme() {
    if (theme.value === 'dark' || (!localStorage.getItem('theme') && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
      document.documentElement.classList.add('dark')
      theme.value = 'dark'
    } else {
      document.documentElement.classList.remove('dark')
      theme.value = 'light'
    }
  }

  function toggleTheme() {
    if (theme.value === 'light') {
      document.documentElement.classList.add('dark')
      theme.value = 'dark'
      localStorage.setItem('theme', 'dark')
    } else {
      document.documentElement.classList.remove('dark')
      theme.value = 'light'
      localStorage.setItem('theme', 'light')
    }
  }

  async function loadPresets() {
    try {
      loading.value = true
      error.value = null

      const { data, error: fetchError } = await supabase
        .from('presets')
        .select('*')
        .order('name')

      if (fetchError) throw fetchError
      presets.value = data || []
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load presets'
      console.error('Error loading presets:', err)
    } finally {
      loading.value = false
    }
  }

  async function loadModels() {
    try {
      loading.value = true
      error.value = null

      const { data, error: fetchError } = await supabase
        .from('models')
        .select('*')
        .eq('is_active', true)
        .order('display_name')

      if (fetchError) throw fetchError
      models.value = data || []
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load models'
      console.error('Error loading models:', err)
    } finally {
      loading.value = false
    }
  }

  async function loadSystemData() {
    await Promise.all([loadPresets(), loadModels()])
  }


  function getPresetById(id: string) {
    return presets.value.find(preset => preset.id === id)
  }

  function getModelById(id: string) {
    return models.value.find(model => model.id === id)
  }


  function getModelsByProvider(provider: string) {
    return models.value.filter(model => model.provider === provider)
  }

  return {
    presets,
    models,
    loading,
    error,
    theme,
    dashboardSidebarWidth,
    setDashboardSidebarWidth,
    initTheme,
    toggleTheme,
    loadPresets,
    loadModels,
    loadSystemData,
    getModelById,
    getPresetById,
    getModelsByProvider
  }
})